import React, {
    type FC,
    forwardRef,
    type ForwardRefExoticComponent,
} from 'react';
import type { ColorValue } from 'react-native';
import type { SvgProps } from 'react-native-svg/lib/typescript/elements/Svg';
import { type GetProps, styled } from 'tamagui';

import {
    type ColorTokenKey,
    useThemeColors,
} from '../../themes/SmoothieColorTokens';

/**
 * Type guard to distinguish between SVG icons and Lucide icons at runtime.
 * SVG icons are FC<SvgProps> (from imported SVG files, see declarations.d.ts).
 * Lucide icons are ForwardRefExoticComponent<LucideProps>.
 *
 * Simple check: ForwardRefExoticComponent has a 'render' property, FC does not.
 */
const isSvgIcon = (icon: IconType): icon is SvgIcon => {
    // Must be a function/component, not an object
    if (typeof icon !== 'function') {
        return false;
    }

    // ForwardRefExoticComponent has a 'render' property that FC does not have
    // If it has render, it's a ForwardRefExoticComponent (Lucide), not an FC (SVG)
    return !(
        'render' in icon &&
        typeof (icon as { render?: unknown }).render === 'function'
    );
};

/**
 * LucideIcon components extend base SvgProps from react-native-svg.
 * Copied from lucide-react-native, where this is defined but importing
 * it instead leads to unexpected type errors.
 */
interface LucideProps extends SvgProps {
    size?: string | number;
    absoluteStrokeWidth?: boolean;
    'data-testid'?: string;
}
type LucideIcon = ForwardRefExoticComponent<LucideProps>;

/**
 * Calculate stroke width based on icon size.
 * Mapping: 16 = 1.5, 24 = 1.5, 32 = 2, 40 = 2.5
 * Linear interpolation for in-between values.
 */
const getStrokeWidth = (size?: string | number): number => {
    if (size == null) {
        return 1.5; // default
    }

    const numericSize = typeof size === 'string' ? parseFloat(size) : size;

    // Exact mappings
    if (numericSize === 16 || numericSize === 24) {
        return 1.5;
    }
    if (numericSize === 32) {
        return 2;
    }
    if (numericSize === 40) {
        return 2.5;
    }

    // Linear interpolation
    if (numericSize < 16) {
        return 1.5; // Use 16's value for smaller sizes
    }
    if (numericSize > 16 && numericSize < 24) {
        return 1.5; // Constant between 16-24
    }
    if (numericSize > 24 && numericSize < 32) {
        // Linear interpolation between 24 (1.5) and 32 (2)
        const t = (numericSize - 24) / (32 - 24);
        return 1.5 + t * (2 - 1.5);
    }
    if (numericSize > 32 && numericSize < 40) {
        // Linear interpolation between 32 (2) and 40 (2.5)
        const t = (numericSize - 32) / (40 - 32);
        return 2 + t * (2.5 - 2);
    }
    // For sizes > 40, continue linear extrapolation
    if (numericSize > 40) {
        const t = (numericSize - 40) / 8; // Continue with same slope
        return 2.5 + t * 0.5;
    }

    return 1.5; // fallback
};

/**
 * A wrapper component for LucideIcons that allows our Icon component to have
 * 'icon' as a prop, but use that prop as the actual component from lucide-react-native
 * (e.g. <Icon icon={CarIcon} /> rather than <CarIcon />) which is useful for us to use
 * in ColorTokenLucideIcon for components that work with our tamagui theme tokens.
 */
const StylableLucideIcon = forwardRef<
    LucideIcon,
    GetProps<LucideIcon> & {
        icon: LucideIcon;
        color?: ColorValue;
        fill?: ColorValue;
    }
>((props, _ref) => {
    const { icon: IconComponent, size, ...rest } = props;
    const strokeWidth = getStrokeWidth(size);
    return (
        <IconComponent
            strokeWidth={strokeWidth}
            absoluteStrokeWidth={true}
            size={size}
            {...rest}
        />
    );
});

/**
 * A version of LucideIcon that supports our tamagui theme token values
 */
const ColorTokenLucideIcon = styled(StylableLucideIcon);

/**
 * A version of ColorTokenLucideIcon that supports our tamagui theme token values,
 * where the 'fill' prop needs special handling (must not be addressed in tamagui's styled function)
 */
const FilledColorTokenLucideIcon = (
    props: Omit<GetProps<typeof ColorTokenLucideIcon>, 'color' | 'fill'> & {
        icon: LucideIcon;
        fill: ColorTokenKey;
        color?: ColorTokenKey;
    },
) => {
    const { getColor } = useThemeColors();
    const { icon, fill, color, ...rest } = props;
    return (
        <ColorTokenLucideIcon
            icon={icon}
            color={color}
            fill={getColor(props.fill)}
            {...rest}
        />
    );
};

/**
 * A wrapper component for SVG icons that allows our Icon component to have
 * 'icon' as a prop, but use that prop as the actual component
 * (e.g. <Icon icon={MoneyMapIcon} /> rather than <MoneyMapIcon />).
 *
 * SVG icons from imported SVG files are typed as FC<SvgProps> (see declarations.d.ts).
 * Converts `size` prop to `width` and `height` props for SVG components.
 * Converts `color` prop to `color` prop which sets currentColor for SVG paths.
 */
const StylableSvgIcon = (
    props: SvgProps & {
        icon: SvgIcon;
        size?: number;
        color?: ColorTokenKey;
        stringColor?: string;
    },
) => {
    const { getColor } = useThemeColors();
    const { icon: IconComponent, size, color, stringColor, ...svgRest } = props;

    // Use stringColor if provided, otherwise resolve color token
    const resolvedColor =
        stringColor ?? (color != null ? getColor(color) : undefined);

    // Convert size to width and height for SVG components
    const svgProps: SvgProps = {
        ...svgRest,
        ...(resolvedColor != null && { color: resolvedColor }),
        ...(size != null && { width: size, height: size }),
    };

    const strokeWidth = getStrokeWidth(size);

    return <IconComponent strokeWidth={strokeWidth} {...svgProps} />;
};

type SmoothieLucideIconProps = Omit<
    GetProps<typeof ColorTokenLucideIcon>,
    'icon' | 'color' | 'fill'
> & {
    /** LucideIcon component imported from 'smoothie', e.g. BananaIcon */
    icon: LucideIcon;
    /** Smoothie color token key for theme-compatible colors. If not provided, default is 'currentColor' */
    color?: ColorTokenKey;
    /** Smoothie color token key for theme-compatible colors. Note: Only some icons will naturally support 'fill' well, where the svg stroke creates a closed shape (e.g. CircleIcon works, but BananaIcon doesn't). */
    fill?: ColorTokenKey;
    /** In the exceptional case where ColorTokenKey values are not available, string values may be provided. Overrides 'color' prop. */
    stringColor?: string;
    /** In the exceptional case where ColorTokenKey values are not available, string values may be provided. Overrides 'fill' prop. Note: Only some icons will naturally support 'fill' well, where the svg stroke creates a closed shape (e.g. CircleIcon works, but BananaIcon doesn't). */
    stringFill?: string;
};

type SmoothieSvgIconProps = Omit<
    GetProps<typeof StylableSvgIcon>,
    'icon' | 'color' | 'stringColor'
> & {
    /** Custom Svg icon component imported from 'smoothie', e.g. MoneyMapIcon */
    icon: SvgIcon;
    /** Smoothie color token key for theme-compatible colors. If not provided, default is 'currentColor' */
    color?: ColorTokenKey;
    /** In the exceptional case where ColorTokenKey values are not available, string values may be provided. Overrides 'color' prop. */
    stringColor?: string;
};

type SvgIcon = FC<SvgProps>;
export type IconType = LucideIcon | SvgIcon;

export type IconProps = (Omit<SmoothieLucideIconProps, 'icon'> &
    Omit<SmoothieSvgIconProps, 'icon'>) & {
    icon: IconType;
};

/**
 * An Icon component that allows us to use LucideIcons and SVG icons with our tamagui themes,
 * where the 'color' and 'fill' props can be provided as ColorTokenKey values.
 * - Only some icons will naturally support 'fill' well, where the svg stroke
 * creates a closed shape (e.g. CircleIcon works, but BananaIcon doesn't).
 * - In the exceptional case where ColorTokenKey values are not available, string
 * values may be provided via 'stringColor' and 'stringFill'.
 * - Supports generic SVG icons in addition to Lucide icons.
 */
export const Icon = (props: IconProps) => {
    const color =
        props.stringColor == null ? (props.color ?? '$black') : undefined;

    // Check for custom SVG icons
    if (isSvgIcon(props.icon)) {
        return (
            <StylableSvgIcon
                {...(props as SmoothieSvgIconProps)}
                color={color}
            />
        );
    }

    // Otherwise, treat as Lucide icon
    const { icon, fill, stringColor, stringFill, ...rest } =
        props as SmoothieLucideIconProps;

    if (stringColor != null || stringFill != null) {
        return (
            <StylableLucideIcon
                icon={icon}
                {...(rest as LucideProps)}
                {...(stringColor != null ? { color: stringColor } : {})}
                {...(stringFill != null ? { fill: stringFill } : {})}
            />
        );
    }

    if (fill != null) {
        return (
            <FilledColorTokenLucideIcon
                icon={icon}
                color={color}
                fill={fill}
                {...rest}
            />
        );
    }

    return <ColorTokenLucideIcon icon={icon} color={color} {...rest} />;
};
